import { __assign, __extends, __rest, __values } from "tslib";
import React, { PureComponent, createRef } from 'react';
import { withBemMod } from '@bem-react/core';
import { canUseDOM } from '../../../lib/canUseDOM';
import { mergeAllRefs } from '../../../lib/mergeRefs';
import { cnPopup } from '../Popup';
var VIEWPORT_ACCURACY_FACTOR = 0.99;
var checkMainDirection = function (direction, mainFirstDirection, mainSecondDirection) {
    return direction.indexOf(mainFirstDirection) === 0 ||
        (mainSecondDirection && direction.indexOf(mainSecondDirection) === 0);
};
var checkSecondaryDirection = function (direction, secondaryDirection) {
    return direction.indexOf('-' + secondaryDirection) > 0;
};
var defaultProps = {
    directions: [
        'bottom-left',
        'bottom-center',
        'bottom-right',
        'top-left',
        'top-center',
        'top-right',
        'right-top',
        'right-center',
        'right-bottom',
        'left-top',
        'left-center',
        'left-bottom',
    ],
    mainOffset: 0,
    secondaryOffset: 0,
    tailOffset: 0,
    viewportOffset: 0,
    scope: { current: canUseDOM() ? document.body : null }
};
var initialPosition = { top: -9999, left: -9999 };
/**
 * Позиционирует попап относительно элемента, который указан в свойстве `anchor`.
 * @param {IPopupTargetAnchorProps} props
 */
export var withTargetAnchor = withBemMod(cnPopup(), { target: 'anchor' }, function (Popup) {
    var _a;
    return _a = /** @class */ (function (_super) {
            __extends(WithTargetAnchor, _super);
            function WithTargetAnchor(props) {
                var _this = _super.call(this, props) || this;
                _this.state = {
                    direction: 'bottom-left',
                    forwarded: {}
                };
                /**
                 * Контейнер с ссылкой на DOM элемент попапа.
                 */
                _this.popupRef = createRef();
                /**
                 * Контейнер с ссылкой на DOM элемент хвостика попапа.
                 */
                _this.tailRef = createRef();
                _this.subscribeToEvents = function () {
                    window.addEventListener('scroll', _this.updateRefsPosition);
                    window.addEventListener('resize', _this.updateRefsPosition);
                    document.addEventListener('documentchange', _this.updateRefsPosition);
                };
                _this.unsubscribeFromEvents = function () {
                    window.removeEventListener('scroll', _this.updateRefsPosition);
                    window.removeEventListener('resize', _this.updateRefsPosition);
                    document.removeEventListener('documentchange', _this.updateRefsPosition);
                };
                /**
                 * Обновлять стили, отвечающие за позиционирование у DOM элемента попапа и хвостика.
                 */
                _this.updateRefsPosition = function () {
                    requestAnimationFrame(function () {
                        var directions = _this.props.directions;
                        var _a = _this.getDrawingParams(directions), direction = _a.direction, popupPosition = _a.popupPosition, tailPosition = _a.tailPosition;
                        if (_this.state.direction !== direction) {
                            _this.setState({ direction: direction });
                        }
                        // Обновляем стили сразу у DOM узла, а не через setState,
                        // т.к. это не вызывает лишний раз re-render и повышает производительность.
                        if (_this.popupRef.current !== null) {
                            _this.popupRef.current.style.top = popupPosition.top + "px";
                            _this.popupRef.current.style.left = popupPosition.left + "px";
                        }
                        if (_this.tailRef.current !== null) {
                            _this.tailRef.current.style.top = tailPosition.top + "px";
                            _this.tailRef.current.style.left = tailPosition.left + "px";
                        }
                    });
                };
                _this.getPossibleDrawingParams = function () {
                    var target = _this.getAnchorDimensions(_this.props.anchor);
                    var viewport = _this.getViewportDimensions();
                    var offsets = _this.getOffsets();
                    return _this.props.directions.map(function (direction) {
                        var res = { direction: direction, width: 0, height: 0, left: 0, top: 0 };
                        if (checkMainDirection(direction, 'bottom')) {
                            res.top = target.top + target.height + offsets.main * 2;
                            res.height = viewport.bottom - res.top - offsets.viewport;
                        }
                        else if (checkMainDirection(direction, 'top')) {
                            res.height = target.top - viewport.top - offsets.main - offsets.viewport;
                            res.top = target.top - res.height - offsets.main;
                        }
                        else {
                            if (checkSecondaryDirection(direction, 'center')) {
                                res.height = viewport.bottom - viewport.top - 2 * offsets.viewport;
                                res.top = target.top + (target.height - res.height) / 2;
                            }
                            else if (checkSecondaryDirection(direction, 'bottom')) {
                                res.height =
                                    target.top + target.height - viewport.top - offsets.secondary - offsets.viewport;
                                res.top = target.top + target.height - res.height - offsets.secondary;
                            }
                            else if (checkSecondaryDirection(direction, 'top')) {
                                res.top = target.top + offsets.secondary;
                                res.height = viewport.bottom - res.top - offsets.viewport;
                            }
                            if (checkMainDirection(direction, 'left')) {
                                res.width = target.left - viewport.left - offsets.main - offsets.viewport;
                                res.left = target.left - res.width - offsets.main;
                            }
                            else {
                                res.left = target.left + target.width + offsets.main;
                                res.width = viewport.right - res.left - offsets.viewport;
                            }
                        }
                        if (checkSecondaryDirection(direction, 'right')) {
                            res.width = target.left + target.width - viewport.left - offsets.secondary - offsets.viewport;
                            res.left = target.left + target.width - res.width - offsets.secondary;
                        }
                        else if (checkSecondaryDirection(direction, 'left')) {
                            res.left = target.left + offsets.secondary;
                            res.width = viewport.right - res.left - offsets.viewport;
                        }
                        else if (checkSecondaryDirection(direction, 'center')) {
                            if (checkMainDirection(direction, 'top', 'bottom')) {
                                res.width = viewport.right - viewport.left - 2 * offsets.viewport;
                                res.left = target.left + target.width / 2 - res.width / 2;
                            }
                        }
                        return res;
                    });
                };
                console.assert(props.anchor !== undefined, 'Для работы "withTargetAnchor" требуется наличие свойства "anchor".');
                console.assert(props.directions.length !== 0, 'Для работы "withTargetAnchor" требуется наличие хотя бы одного направления для раскрытия.');
                return _this;
            }
            WithTargetAnchor.prototype.componentDidMount = function () {
                if (this.props.visible) {
                    this.setState({ forwarded: {} });
                }
                if (this.shouldSubscribeToEvents()) {
                    this.subscribeToEvents();
                }
            };
            WithTargetAnchor.prototype.componentDidUpdate = function (prevProps, prevState) {
                var _this = this;
                // При ленивой инициализации на первом рендере не проставляются ссылки на контейнер.
                if (this.popupRef.current === null && this.props.visible) {
                    requestAnimationFrame(function () {
                        _this.setState({ forwarded: {} });
                    });
                }
                if (prevProps.visible !== this.props.visible ||
                    prevProps.anchor !== this.props.anchor ||
                    prevState.forwarded !== this.state.forwarded) {
                    this.updateRefsPosition();
                }
                if (this.shouldSubscribeToEvents()) {
                    this.subscribeToEvents();
                }
                else {
                    this.unsubscribeFromEvents();
                }
            };
            WithTargetAnchor.prototype.componentWillUnmount = function () {
                this.unsubscribeFromEvents();
            };
            WithTargetAnchor.prototype.render = function () {
                var _a = this.props, anchor = _a.anchor, directions = _a.directions, mainOffset = _a.mainOffset, motionless = _a.motionless, secondaryOffset = _a.secondaryOffset, tailOffset = _a.tailOffset, target = _a.target, viewportOffset = _a.viewportOffset, getPossibleDrawingParams = _a.getPossibleDrawingParams, props = __rest(_a, ["anchor", "directions", "mainOffset", "motionless", "secondaryOffset", "tailOffset", "target", "viewportOffset", "getPossibleDrawingParams"]);
                if (canUseDOM() && getPossibleDrawingParams !== undefined) {
                    getPossibleDrawingParams(this.getPossibleDrawingParams());
                }
                return (React.createElement(Popup, __assign({}, props, { direction: this.state.direction, innerRef: mergeAllRefs(this.popupRef, this.props.innerRef), tailRef: mergeAllRefs(this.tailRef, this.props.tailRef), 
                    // Выставляем отрицательные координаты при первой отрисовке,
                    // для того, чтобы не было морганий при монтировании компонента,
                    // в дальнейшем позиция будет обновляться напрямую в DOM узле.
                    position: initialPosition })));
            };
            WithTargetAnchor.prototype.shouldSubscribeToEvents = function () {
                return this.props.visible && !this.props.motionless;
            };
            /* eslint-disable max-len */
            /**
             * Вычисляет лучшие параметры отображения. Под лучшими параметрами понимаются параметры для первого подходящего
             * направления из directions, которое может обеспечить размещение 99% (VIEWPORT_ACCURACY_FACTOR) площади попапа.
             * Если подходящего направления не найдено, то предпочтение отдается направлению, в котором попап открывался
             * последний раз, или первому направлению из списка.
             *
             * @param directions Массив направлений
             */
            /* eslint-enable max-len */
            WithTargetAnchor.prototype.getDrawingParams = function (directions) {
                var e_1, _a;
                var params = {
                    direction: 'bottom-left',
                    popupPosition: { top: 0, left: 0 },
                    tailPosition: { top: 0, left: 0 }
                };
                var popupDimensions = this.getPopupDimensions(this.popupRef);
                var anchorDimensions = this.getAnchorDimensions(this.props.anchor);
                var viewportDimensions = this.getViewportDimensions();
                var scopeDimensions = this.getScopeDimensions(this.props.scope);
                var viewportFactor = 0;
                try {
                    for (var directions_1 = __values(directions), directions_1_1 = directions_1.next(); !directions_1_1.done; directions_1_1 = directions_1.next()) {
                        var nextDirection = directions_1_1.value;
                        var popupPosition = this.getPopupPosition(nextDirection, anchorDimensions, popupDimensions);
                        var nextViewportFactor = this.getViewportFactor(popupPosition, viewportDimensions, popupDimensions);
                        if (nextViewportFactor > viewportFactor || this.state.direction === nextDirection) {
                            params.popupPosition = {
                                top: popupPosition.top - (scopeDimensions.top + window.pageYOffset),
                                left: popupPosition.left - (scopeDimensions.left + window.pageXOffset)
                            };
                            params.direction = nextDirection;
                            viewportFactor = nextViewportFactor;
                        }
                        if (viewportFactor > VIEWPORT_ACCURACY_FACTOR) {
                            break;
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (directions_1_1 && !directions_1_1.done && (_a = directions_1["return"])) _a.call(directions_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
                if (this.tailRef.current !== null) {
                    params.tailPosition = this.getTailPosition(params.direction, anchorDimensions, popupDimensions);
                }
                return params;
            };
            /**
             * Вычисляет размеры попапа.
             *
             * @param popupRef Ссылка на DOM элемент попапа.
             */
            WithTargetAnchor.prototype.getPopupDimensions = function (popupRef) {
                var dimensions = {
                    area: 0,
                    height: 0,
                    width: 0,
                    popupHeight: 0,
                    popupWidth: 0
                };
                if (popupRef !== undefined && popupRef.current !== null) {
                    var _a = popupRef.current, clientWidth = _a.clientWidth, clientHeight = _a.clientHeight;
                    var _b = popupRef.current.getBoundingClientRect(), width = _b.width, height = _b.height;
                    dimensions.area = clientWidth * clientHeight;
                    // Погрешность расчета при рендеринге ie11
                    dimensions.height = Math.round(height);
                    // Погрешность при расчете offsetWidth при рендеринге в chrome
                    dimensions.width = Math.round(width);
                    dimensions.popupHeight = clientHeight;
                    dimensions.popupWidth = clientWidth;
                }
                return dimensions;
            };
            /**
             * Вычисляет размеры анкора.
             *
             * @param anchorRef Ссылка на DOM элемент анкора.
             */
            WithTargetAnchor.prototype.getAnchorDimensions = function (anchorRef) {
                var dimensions = {
                    left: 0,
                    top: 0,
                    height: 0,
                    width: 0
                };
                // Рассчитываем позицию анкора относительно его родителя чтобы не было
                // неточностей в координатах, например из-за transform scale.
                var anchorNode = anchorRef && anchorRef.current;
                var parentNode = anchorNode && anchorNode.offsetParent;
                if (anchorNode && parentNode) {
                    var _a = parentNode.getBoundingClientRect(), parentLeft = _a.left, parentTop = _a.top;
                    dimensions.left = parentLeft + anchorNode.offsetLeft + window.pageXOffset;
                    dimensions.top = parentTop + anchorNode.offsetTop + window.pageYOffset;
                    dimensions.height = anchorNode.offsetHeight;
                    dimensions.width = anchorNode.offsetWidth;
                }
                return dimensions;
            };
            /**
             * Вычисляет размеры хвостика.
             */
            WithTargetAnchor.prototype.getTailDimensions = function () {
                var dimensions = { size: 0 };
                if (this.props.tailSize !== undefined) {
                    dimensions.size = this.props.tailSize;
                }
                if (this.tailRef.current !== null) {
                    dimensions.size = this.tailRef.current.offsetWidth;
                }
                return dimensions;
            };
            /**
             * Вычисляет размеры окна.
             */
            WithTargetAnchor.prototype.getViewportDimensions = function () {
                return {
                    top: window.pageYOffset,
                    left: window.pageXOffset,
                    bottom: window.pageYOffset + window.innerHeight,
                    right: window.pageXOffset + window.innerWidth
                };
            };
            /**
             * Вычисляет размеры области.
             *
             * @param scopeRef Ссылка на DOM элемент области.
             */
            WithTargetAnchor.prototype.getScopeDimensions = function (scopeRef) {
                var dimensions = { top: 0, left: 0 };
                if (scopeRef !== undefined && scopeRef.current !== null) {
                    var _a = scopeRef.current.getBoundingClientRect(), left = _a.left, top_1 = _a.top;
                    dimensions.top = top_1;
                    dimensions.left = left;
                }
                return dimensions;
            };
            /**
             * Вычисляет координаты попапа для заданного направления.
             *
             * @param direction Текущее направление в котором раскрыт попап
             * @param anchorDimensions Размеры анкора
             * @param popupDimensions Размеры попапа
             */
            WithTargetAnchor.prototype.getPopupPosition = function (direction, anchorDimensions, popupDimensions) {
                var position = { left: 0, top: 0 };
                var offsets = this.getOffsets();
                if (checkMainDirection(direction, 'bottom')) {
                    position.top = anchorDimensions.top + anchorDimensions.height + offsets.main;
                }
                else if (checkMainDirection(direction, 'top')) {
                    position.top = anchorDimensions.top - popupDimensions.height - offsets.main;
                }
                else if (checkMainDirection(direction, 'left')) {
                    position.left = anchorDimensions.left - popupDimensions.width - offsets.main;
                }
                else if (checkMainDirection(direction, 'right')) {
                    position.left = anchorDimensions.left + anchorDimensions.width + offsets.main;
                }
                if (checkSecondaryDirection(direction, 'right')) {
                    position.left =
                        anchorDimensions.left + anchorDimensions.width - popupDimensions.width - offsets.secondary;
                }
                else if (checkSecondaryDirection(direction, 'left')) {
                    position.left = anchorDimensions.left + offsets.secondary;
                }
                else if (checkSecondaryDirection(direction, 'bottom')) {
                    position.top =
                        anchorDimensions.top + anchorDimensions.height - popupDimensions.height - offsets.secondary;
                }
                else if (checkSecondaryDirection(direction, 'top')) {
                    position.top = anchorDimensions.top + offsets.secondary;
                }
                else if (checkSecondaryDirection(direction, 'center')) {
                    if (checkMainDirection(direction, 'top', 'bottom')) {
                        position.left = anchorDimensions.left + anchorDimensions.width / 2 - popupDimensions.width / 2;
                    }
                    else if (checkMainDirection(direction, 'left', 'right')) {
                        position.top = anchorDimensions.top + anchorDimensions.height / 2 - popupDimensions.height / 2;
                    }
                }
                return position;
            };
            /**
             * Вычисляет координаты хвостика для заданного направления.
             *
             * @param direction Текущее направление в котором раскрыт попап
             * @param anchorDimensions Размеры анкора
             * @param popupDimensions Размеры попапа
             */
            WithTargetAnchor.prototype.getTailPosition = function (direction, anchorDimensions, popupDimensions) {
                var position = { left: 0, top: 0 };
                var tailDimensions = this.getTailDimensions();
                var halfSizeOfTail = tailDimensions.size / 2;
                var offsets = this.getOffsets();
                if (checkMainDirection(direction, 'bottom')) {
                    position.top = -halfSizeOfTail;
                }
                else if (checkMainDirection(direction, 'top')) {
                    position.top = Math.floor(popupDimensions.popupHeight - halfSizeOfTail);
                }
                else if (checkMainDirection(direction, 'left')) {
                    position.left = Math.floor(popupDimensions.popupWidth - halfSizeOfTail);
                }
                else if (checkMainDirection(direction, 'right')) {
                    position.left = -halfSizeOfTail;
                }
                if (checkSecondaryDirection(direction, 'right')) {
                    position.left =
                        popupDimensions.popupWidth -
                            Math.ceil(Math.min(popupDimensions.popupWidth, anchorDimensions.width) / 2) -
                            halfSizeOfTail -
                            offsets.tail;
                }
                else if (checkSecondaryDirection(direction, 'left')) {
                    position.left =
                        Math.ceil(Math.min(popupDimensions.popupWidth, anchorDimensions.width) / 2) -
                            halfSizeOfTail +
                            offsets.tail;
                }
                else if (checkSecondaryDirection(direction, 'bottom')) {
                    position.top =
                        popupDimensions.popupHeight -
                            Math.ceil(Math.min(popupDimensions.popupHeight, anchorDimensions.height) / 2) -
                            halfSizeOfTail -
                            offsets.tail;
                }
                else if (checkSecondaryDirection(direction, 'top')) {
                    position.top =
                        Math.ceil(Math.min(popupDimensions.popupHeight, anchorDimensions.height) / 2) -
                            halfSizeOfTail +
                            offsets.tail;
                }
                else if (checkSecondaryDirection(direction, 'center')) {
                    if (checkMainDirection(direction, 'top', 'bottom')) {
                        position.left = Math.ceil(popupDimensions.popupWidth / 2) - halfSizeOfTail + offsets.tail;
                    }
                    else {
                        position.top = Math.ceil(popupDimensions.popupHeight / 2) - halfSizeOfTail + offsets.tail;
                    }
                }
                return position;
            };
            WithTargetAnchor.prototype.getOffsets = function () {
                var _a = this.props, mainOffset = _a.mainOffset, secondaryOffset = _a.secondaryOffset, tailOffset = _a.tailOffset, viewportOffset = _a.viewportOffset;
                var tailDimensions = this.getTailDimensions();
                var computedMainOffset = mainOffset >= 0 && tailDimensions.size > 0
                    ? Math.max(mainOffset, Math.round(tailDimensions.size * Math.SQRT1_2))
                    : mainOffset;
                return {
                    main: computedMainOffset,
                    secondary: secondaryOffset,
                    tail: tailOffset,
                    viewport: viewportOffset
                };
            };
            /**
             * Вычисляет коэффициент пересечения доступной площади для открытия попапа и собственной площади попапа.
             *
             * @param popupPosition Позиция попапа
             * @param viewportDimensions Размеры окна
             * @param popupDimensions Размеры попапа
             */
            WithTargetAnchor.prototype.getViewportFactor = function (popupPosition, viewportDimensions, popupDimensions) {
                var viewportFactor = 0;
                var offsets = this.getOffsets();
                var intersectionLeft = Math.max(popupPosition.left, viewportDimensions.left + offsets.viewport);
                var intersectionRight = Math.min(popupPosition.left + popupDimensions.width, viewportDimensions.right - offsets.viewport);
                var intersectionTop = Math.max(popupPosition.top, viewportDimensions.top + offsets.viewport);
                var intersectionBottom = Math.min(popupPosition.top + popupDimensions.height, viewportDimensions.bottom - offsets.viewport);
                if (intersectionLeft < intersectionRight && intersectionTop < intersectionBottom) {
                    viewportFactor =
                        ((intersectionRight - intersectionLeft) * (intersectionBottom - intersectionTop)) /
                            popupDimensions.area;
                }
                return viewportFactor;
            };
            return WithTargetAnchor;
        }(PureComponent)),
        _a.defaultProps = defaultProps,
        _a;
});
